import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const GIFT_PROP = '_threshold_gift';
const SOURCE = 'cart-threshold-gift';

/**
 * Auto-adds / removes gift products for one or more cart thresholds.
 * Steps come from data-steps JSON on <cart-threshold-gift>.
 */
class CartThresholdGift extends HTMLElement {
  /** @type {boolean} */
  #busy = false;

  /** @type {number | null} */
  #queued = null;

  /** @type {Array<{ id: string, variantId: string, thresholdCents: number, label: string }>} */
  #steps = [];

  connectedCallback() {
    if (this.dataset.enabled !== 'true') return;
    // Theme editor remounts sections constantly — gift cart sync causes visible flicker loops.
    if (window.Shopify?.designMode) return;

    this.#steps = this.#parseSteps();
    if (!this.#steps.length) return;

    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    window.addEventListener('pageshow', this.#onPageShow);
    this.#scheduleSync(50);
  }

  disconnectedCallback() {
    document.removeEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate);
    window.removeEventListener('pageshow', this.#onPageShow);
    if (this.#queued) window.clearTimeout(this.#queued);
  }

  #parseSteps() {
    try {
      const raw = this.dataset.steps || '[]';
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return [];
      return parsed
        .map((step) => ({
          id: String(step.id || ''),
          variantId: String(step.variantId || ''),
          thresholdCents: Math.max(0, Number(step.thresholdCents) || 0),
          label: String(step.label || 'Hediye'),
        }))
        .filter((step) => step.id && step.variantId && step.thresholdCents > 0);
    } catch (error) {
      console.warn('[cart-threshold-gift] invalid steps json', error);
      return [];
    }
  }

  #onPageShow = (event) => {
    if (event.persisted) this.#scheduleSync(0);
  };

  /**
   * @param {CartLinesUpdateEvent} event
   */
  #onCartUpdate = (event) => {
    if (this.#busy) return;

    const run = () => this.#scheduleSync(80);
    if (event?.promise) {
      event.promise.then(run).catch(run);
    } else {
      run();
    }
  };

  /**
   * @param {number} delay
   */
  #scheduleSync(delay) {
    if (this.#queued) window.clearTimeout(this.#queued);
    this.#queued = window.setTimeout(() => {
      this.#queued = null;
      this.#sync();
    }, delay);
  }

  async #sync() {
    if (this.#busy || this.dataset.enabled !== 'true' || !this.#steps.length) return;

    this.#busy = true;

    try {
      let cart = await this.#fetchCart();
      if (!cart) return;

      // Process lowest threshold first so both gifts can stack cleanly
      const steps = [...this.#steps].sort((a, b) => a.thresholdCents - b.thresholdCents);

      for (const step of steps) {
        cart = (await this.#syncStep(cart, step)) || cart;
      }
    } catch (error) {
      console.warn('[cart-threshold-gift]', error);
    } finally {
      this.#busy = false;
    }
  }

  /**
   * @param {object} cart
   * @param {{ id: string, variantId: string, thresholdCents: number, label: string }} step
   */
  async #syncStep(cart, step) {
    const giftLines = (cart.items || []).filter((item) => this.#isGiftLine(item, step.id));
    const eligibleTotal = (cart.items || []).reduce((sum, item) => {
      if (this.#isAnyGiftLine(item)) return sum;
      return sum + Number(item.final_line_price || item.line_price || 0);
    }, 0);

    const qualifies = eligibleTotal >= step.thresholdCents;
    const giftQty = giftLines.reduce((sum, item) => sum + Number(item.quantity || 0), 0);

    if (qualifies && giftQty === 0) {
      return this.#addGift(step);
    }

    if (qualifies && giftQty > 1) {
      const primary = giftLines[0];
      if (primary) {
        cart = (await this.#changeLine(primary.key || primary.id, 1)) || cart;
        for (let i = 1; i < giftLines.length; i += 1) {
          const extra = giftLines[i];
          if (extra) cart = (await this.#changeLine(extra.key || extra.id, 0)) || cart;
        }
      }
      return cart;
    }

    if (!qualifies && giftQty > 0) {
      for (const line of giftLines) {
        cart = (await this.#changeLine(line.key || line.id, 0)) || cart;
      }
      return cart;
    }

    return cart;
  }

  /**
   * @param {object} item
   * @param {string} [stepId]
   */
  #isGiftLine(item, stepId) {
    const props = item?.properties;
    if (!props) return false;
    const value = props[GIFT_PROP];
    if (value == null || value === '' || value === false) return false;
    if (stepId) return String(value) === String(stepId);
    return true;
  }

  /**
   * @param {object} item
   */
  #isAnyGiftLine(item) {
    return this.#isGiftLine(item);
  }

  async #fetchCart() {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
    });
    if (!response.ok) return null;
    return response.json();
  }

  /**
   * @returns {string}
   */
  #sectionIds() {
    return Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : null))
      .filter(Boolean)
      .join(',');
  }

  /**
   * @param {{ id: string, variantId: string, label: string }} step
   */
  async #addGift(step) {
    const sectionIds = this.#sectionIds();
    const deferred = CartLinesUpdateEvent.createPromise();

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(step.variantId), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [
            {
              id: Number(step.variantId),
              quantity: 1,
              properties: {
                [GIFT_PROP]: String(step.id),
              },
            },
          ],
          ...(sectionIds ? { sections: sectionIds } : {}),
        }),
      });

      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Gift add failed');
      }

      const cart = await this.#fetchCart();
      if (!cart) throw new Error('Cart refresh failed');

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: cart.item_count,
          sections: data.sections,
          source: SOURCE,
          didError: false,
        },
      });

      return cart;
    } catch (error) {
      deferred.reject(error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  }

  /**
   * @param {string | number} lineKey
   * @param {number} quantity
   */
  async #changeLine(lineKey, quantity) {
    const sectionIds = this.#sectionIds();
    const deferred = CartLinesUpdateEvent.createPromise();
    const action = quantity <= 0 ? 'remove' : 'change';

    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action,
        context: 'cart',
        lines: [{ merchandiseId: String(lineKey), quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const changeUrl = Theme.routes.cart_change_url.includes('.js')
        ? Theme.routes.cart_change_url
        : `${Theme.routes.cart_change_url}.js`;

      const response = await fetch(changeUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: String(lineKey),
          quantity,
          ...(sectionIds ? { sections: sectionIds } : {}),
        }),
      });

      const data = await response.json();
      if (!response.ok || data.status) {
        throw new Error(data.description || data.message || 'Gift change failed');
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(data),
        detail: {
          items: data.items,
          itemCount: data.item_count,
          sections: data.sections,
          source: SOURCE,
          didError: false,
        },
      });

      return data;
    } catch (error) {
      deferred.reject(error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  }
}

if (!customElements.get('cart-threshold-gift')) {
  customElements.define('cart-threshold-gift', CartThresholdGift);
}
